import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MessageSquarePlus, ExternalLink } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { base44 } from '@/api/base44Client';
import ReviewFormModal from './ReviewFormModal';

const GROUP_SIZE = 3;
const CACHE_KEY = 'jh_reviews_v1';
const readCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || []; } catch { return []; } };

export default function ReviewsSection() {
  const es = useLang().language === 'es';
  const [reviews, setReviews] = useState(readCache);
  const [index, setIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Warm the browser cache so photos are ready before each group rotates in.
  useEffect(() => { reviews.forEach((r) => { if (r.photo_url) new Image().src = r.photo_url; }); }, [reviews]);

  useEffect(() => {
    let active = true;
    base44.entities.Review.list('-created_date')
      // Hide unverified placeholder testimonials (stock randomuser.me photos).
      .then(data => {
        const clean = data.filter(r => !r.photo_url?.includes('randomuser.me'));
        localStorage.setItem(CACHE_KEY, JSON.stringify(clean));
        if (active) setReviews(clean);
      })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const groups = useMemo(() => {
    if (reviews.length === 0) return [];
    return Array.from({ length: Math.ceil(reviews.length / GROUP_SIZE) }, (_, i) =>
      reviews.slice(i * GROUP_SIZE, i * GROUP_SIZE + GROUP_SIZE)
    );
  }, [reviews]);

  useEffect(() => {
    if (groups.length < 2) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % groups.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [groups.length]);

  const handleSaved = (newReview) => {
    setReviews((prev) => [newReview, ...prev]);
    setIndex(0);
    setShowModal(false);
  };

  return (
    <section id="reviews" className="relative py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16">
          <span className="text-xs font-inter font-medium tracking-[0.3em] uppercase text-primary mb-4 block">
            Testimonials
          </span>
          <h2 className="font-inter font-black text-4xl md:text-5xl tracking-[-0.03em] text-foreground mb-4">
            What People Say
          </h2>
          <div className="w-16 h-px bg-primary/40 mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 min-h-[280px]">
          {groups.length === 0 && <p role="status" className="md:col-span-3 self-center text-center text-muted-foreground">
            {loading ? 'Loading reviews...' : loadError ? 'Reviews could not be loaded. Please refresh to try again.' : 'Be the first to share your experience.'}
          </p>}
          <AnimatePresence mode="wait">
            {groups[index]?.map((review, i) => (
              <motion.div
                key={`${review.id || i}-${index}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: 'easeOut' } }}
                exit={{ opacity: 0, y: -12, transition: { duration: 0.5, delay: i * 0.08, ease: 'easeIn' } }}
                className="p-6 rounded-2xl border border-border/50 bg-card flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  {review.photo_url ? (
                    <img src={review.photo_url} alt={review.name} width="48" height="48" decoding="async" className="w-12 h-12 rounded-full object-cover border border-primary/20" />
                  ) : (
                    <div aria-hidden="true" className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm border border-primary/20">
                      {(review.name || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-inter font-semibold text-foreground text-sm">{review.name}</p>
                    <div className="flex gap-0.5 mt-0.5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className={`w-3.5 h-3.5 ${s < (review.rating || 5) ? 'fill-primary text-primary' : 'text-muted-foreground/30'}`} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground/85 font-inter leading-relaxed">{review.comment}</p>
                {review.project_url && (
                  <a href={review.project_url} target="_blank" rel="noopener noreferrer nofollow" className="mt-auto self-center inline-flex items-center gap-1.5 text-xs font-inter font-semibold tracking-wider uppercase text-primary hover:underline">
                    <ExternalLink className="w-3.5 h-3.5" />
                    {es ? 'Proyecto' : 'Project'}
                  </a>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="flex justify-center mt-12">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-inter font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20">
            <MessageSquarePlus className="w-4 h-4" />
            Leave a Review
          </button>
        </div>
      </div>

      {showModal && (
        <ReviewFormModal onClose={() => setShowModal(false)} onSaved={handleSaved} />
      )}
    </section>
  );
}